/**
 * The trainer's brand colour on the signed-in app: when the read starts, how long the shell may be
 * held for it, what paints it, and what takes it off again.
 *
 * The requirement (owner, 23 September 2026) is that the app never shows a frame that later
 * changes colour: hold, then appear correct, never appear and repaint. Four pieces, in the order a
 * page load meets them:
 *
 * | Piece | When | What |
 * |---|---|---|
 * | `public/theme-init.js` | before first paint | Lays the pointed account's remembered pair over the theme class. |
 * | `startBrandColorPrefetch` | each hydration | Starts `GET /me/brand-color` beside `/auth/me`; a NEW session first drops the previous session's answer. |
 * | `holdForBrandPaint` | `_shell`'s `beforeLoad` | The session's first entry, for an account this browser has never seen, waits for the read, at most `BRAND_PAINT_WAIT_MS`. |
 * | `useBrandPaint` | the shell, in a layout effect | Paints the answer (or the hint until it lands), remembers it, points the pre-paint at this account. |
 *
 * ── Only the shell paints ────────────────────────────────────────────────────────────────────
 * Every pre-session screen wears the default Flame (owner). The session turns `signedIn` while the
 * sign-in screen is still the one on screen, so a painter keyed on the session would recolour that
 * screen the moment the read landed. The shell is what mounts when the app is really entered, and
 * its unmount is what leaves it, so the colour follows the shell: laid on in a layout effect before
 * its first frame, taken off in the cleanup before the next screen's first frame. The one thing the
 * shell cannot catch is a page load that never reaches it, which `useDefaultPaintOutsideTheApp`
 * covers from the root.
 *
 * Every decision is `entities/brand-color`'s `brandPaintGate` and is tested there; this file keeps
 * the one piece of state it needs (`session`) and does the I/O.
 */

import { useQuery } from '@tanstack/react-query';
import { useLayoutEffect } from 'react';

import {
  NO_BRAND_PAINT_SESSION,
  brandColorQueryKey,
  brandColorQueryOptions,
  brandHydrationStarted,
  brandPaintFor,
  brandReadSettled,
  brandShellEntry,
  pointBrandPaintAt,
  readBrandPaintAccount,
  readBrandPaintHint,
  rememberBrandPaint,
  type BrandPaintSession,
} from '~/entities/brand-color';
import {
  onSessionHydrationStart,
  readSession,
  routeForSessionStatus,
  useSessionStatus,
  useSessionUser,
  type SessionStatus,
} from '~/entities/session';
import { setThemeOverrides } from '~/shared/design-system/theme';
import { preferenceStore } from '~/shared/lib/preferenceStore';
import { queryClient } from '~/shared/lib/queryClient';

/**
 * How long a first sign-in in this browser may hold the shell for its colour — the mobile app's
 * `BRAND_PAINT_WAIT_MS`. About two hundred times the endpoint's measured server time, and the read
 * is already in flight with `/auth/me`, so a working network never reaches it. A slow or failing
 * one gets the default now and the real colour when it lands, rather than a stall.
 */
const BRAND_PAINT_WAIT_MS = 1000;

/** Where this page load stands, for the gate. One tab, one value; see `brandPaintGate.ts`. */
let session: BrandPaintSession = NO_BRAND_PAINT_SESSION;

/** The statuses that live inside the shell: the ones the session's own routing sends to `/`. */
function isInsideTheApp(status: SessionStatus): boolean {
  return routeForSessionStatus(status) === '/';
}

/** Take the colour off `<html>` and stop the pre-paint pointing at anyone. */
function clearBrandPaint(): void {
  setThemeOverrides(null);
  pointBrandPaintAt(preferenceStore(), null);
}

/**
 * Start the colour's read with every hydration. Called once from `main.tsx`, before the session
 * starts resolving. That includes an in-app `rehydrateSession` or a degraded recovery, which
 * refetch the colour on purpose and keep the cached answer while they do. A hydration that opens a
 * NEW session drops the cached answer first: it may be the previous account's.
 */
export function startBrandColorPrefetch(): void {
  onSessionHydrationStart(() => {
    const started = brandHydrationStarted(session, isInsideTheApp(readSession().status));
    session = started.state;
    if (started.dropRead) queryClient.removeQueries({ queryKey: brandColorQueryKey });
    const generation = session.generation;
    // `prefetchQuery` never rejects: it resolves once the read has settled either way.
    void queryClient.prefetchQuery({ ...brandColorQueryOptions(), staleTime: 0 }).then(() => {
      session = brandReadSettled(session, generation);
    });
  });
}

/**
 * The session's first shell entry holds for the colour when the gate says so, for at most
 * `BRAND_PAINT_WAIT_MS`. The router shows the colour-agnostic `SessionResolvingScreen` if the wait
 * outlasts its pending delay. Every later entry in the session passes straight through.
 */
export async function holdForBrandPaint(): Promise<void> {
  if (session.held) return;
  const user = readSession().user;
  if (user === null) return;
  const entry = brandShellEntry(session, readBrandPaintHint(preferenceStore(), user.id) !== null);
  session = entry.state;
  if (!entry.hold) return;

  // `fetchQuery` joins the prefetch already in flight rather than starting a second request. The
  // cap's timer outlives a fast answer by up to a second; it only resolves a promise nobody awaits.
  await Promise.race([
    queryClient.fetchQuery(brandColorQueryOptions()).catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, BRAND_PAINT_WAIT_MS)),
  ]);
}

/**
 * The shell's painter. Paints what `brandPaintFor` decides, writes a fresh answer back, and keeps
 * the pre-paint pointed at this account for as long as the shell stands.
 */
export function useBrandPaint(): void {
  const status = useSessionStatus();
  const accountId = useSessionUser()?.id ?? null;
  const inside = isInsideTheApp(status);
  const read = useQuery({ ...brandColorQueryOptions(), enabled: inside });
  const answer = read.data;

  useLayoutEffect(() => {
    const store = preferenceStore();
    if (!inside) {
      // Signed out while the shell still stands (the account screen awaiting its navigation):
      // the colour stays until the shell goes, so no frame of it turns Flame, but a reload from
      // here must not pre-paint the next screen.
      pointBrandPaintAt(store, null);
      return;
    }
    // A degraded boot has no user yet; the pointer is the account whose token this is.
    const account = accountId ?? readBrandPaintAccount(store);
    if (account === null) return;

    const remembered = answer === undefined ? readBrandPaintHint(store, account) : null;
    const decision = brandPaintFor(answer, remembered);
    setThemeOverrides(decision.paint);
    if (decision.fresh) rememberBrandPaint(store, account, decision.paint);
    pointBrandPaintAt(store, account);
  }, [inside, accountId, answer]);

  useLayoutEffect(() => clearBrandPaint, []);
}

/**
 * The root's half: a page load that settles OUTSIDE the app (a session that expired while the tab
 * was closed, a refused role) takes off whatever the pre-paint laid on before the sign-in screen's
 * first frame. Once, at mount, because a later change of status happens with the shell on screen,
 * and the shell's own cleanup is what must decide that frame.
 */
export function useDefaultPaintOutsideTheApp(): void {
  useLayoutEffect(() => {
    if (!isInsideTheApp(readSession().status)) clearBrandPaint();
  }, []);
}
